import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Signature, Wallet } from 'ethers';
import { LanguageSelect } from '../components/LanguageSelect';
import { ChainSelect } from '../components/form/ChainSelect';
import { WalletSelect } from '../components/form/WalletSelect';
import {
  inputFieldClass,
  inputMonoFieldClass,
  labelClass,
  outlineSoftButtonClass,
  primarySubmitButtonClass,
  selectFieldClass,
  textareaFieldClass,
} from '../components/form/fieldClasses';
import {
  type CatalogResponse,
  type CatalogToken,
  getJSON,
  postJSON,
  type ProxyTransferPendingNonceResponse,
  type ProxyTransferPrepareResponse,
  type ProxyTransferSubmitResponse,
  type TransferNetworkResponse,
  type TransferPendingNonceResponse,
  type TransferTransactionInfo,
  type UserProfile,
  type WalletsResponse,
} from '../lib/api';
import { CHAIN_ID_BY_KEY } from '../lib/chainList';
import { explorerTxUrl } from '../lib/explorers';
import { prefillEip1559Gwei } from '../lib/gasFees';
import { useWalletSessionUnlocked } from '../hooks/useWalletSessionUnlocked';
import { isWalletSessionUnlocked } from '../lib/mpcUnlockCache';
import { signDigestWithWalletMpc } from '../lib/mpcSession';
import { appMainClass, pageWrapClass } from '../layout/page';

function parseNonceInput(raw: string): number | undefined {
  const t = raw.trim();
  if (!t) {
    return undefined;
  }
  if (!/^\d+$/.test(t)) {
    throw new Error('INVALID_NONCE');
  }
  const n = Number(t);
  if (!Number.isSafeInteger(n) || n < 0) {
    throw new Error('INVALID_NONCE');
  }
  return n;
}

function normalizePk(raw: string): string {
  const t = raw.trim();
  if (!t) {
    return '';
  }
  return t.startsWith('0x') ? t : `0x${t}`;
}

type Row = { token_ref: string; amount: string };

export default function ProxyTransferPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState<WalletsResponse['wallets']>([]);
  const [catalogTokens, setCatalogTokens] = useState<CatalogToken[]>([]);
  const [walletId, setWalletId] = useState<number>(0);
  const payUnlocked = useWalletSessionUnlocked(walletId);
  const [chain, setChain] = useState<string>('ethereum');
  const [privateKey, setPrivateKey] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [rows, setRows] = useState<Row[]>([{ token_ref: 'native', amount: '' }]);
  const [maxFeeGwei, setMaxFeeGwei] = useState('2');
  const [priorityFeeGwei, setPriorityFeeGwei] = useState('1');
  const [paymentPassword, setPaymentPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [profile, setProfile] = useState<Pick<UserProfile, 'mfa_enabled'> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [prepSummary, setPrepSummary] = useState<ProxyTransferPrepareResponse | null>(null);
  const [builtTxJson, setBuiltTxJson] = useState<TransferTransactionInfo | null>(null);
  const [nonceInput, setNonceInput] = useState('');
  const [nonceLoadErr, setNonceLoadErr] = useState('');
  const nonceReqGen = useRef(0);
  const sourceNonceReqGen = useRef(0);
  const nonceDirtyRef = useRef(false);
  const [sourceNonce, setSourceNonce] = useState<number | null>(null);
  const [baseFeeLine, setBaseFeeLine] = useState('');
  const feeReqGen = useRef(0);

  const loadBootstrap = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const [wr, cr, pr] = await Promise.all([
        getJSON<WalletsResponse>('/api/user/wallets'),
        getJSON<CatalogResponse>('/api/user/swap/catalog'),
        getJSON<UserProfile>('/api/user/profile'),
      ]);
      if (wr.res.status === 401 || pr.res.status === 401) {
        navigate('/', { replace: true });
        return;
      }
      if (wr.res.status === 403 || pr.res.status === 403) {
        navigate('/home', { replace: true });
        return;
      }
      if (pr.res.ok) {
        setProfile({ mfa_enabled: pr.data.mfa_enabled });
      } else {
        setProfile(null);
      }
      if (!wr.res.ok) {
        setMsgOk(false);
        setMsg(wr.data.error || t('common.error'));
        setWallets([]);
      } else {
        setWallets(wr.data.wallets ?? []);
      }
      if (!cr.res.ok) {
        setCatalogTokens([]);
        setMsgOk(false);
        setMsg((m) => m || cr.data.error || t('common.error'));
      } else {
        setCatalogTokens(cr.data.tokens ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    void loadBootstrap();
  }, [loadBootstrap, i18n.language]);

  useEffect(() => {
    const raw = searchParams.get('wallet');
    const n = raw ? Number(raw) : NaN;
    if (!Number.isFinite(n) || n <= 0 || wallets.length === 0) {
      return;
    }
    if (!wallets.some((w) => w.id === n)) {
      return;
    }
    setWalletId(n);
  }, [searchParams, wallets]);

  const selectedWallet = useMemo(
    () => wallets.find((w) => w.id === walletId) ?? null,
    [wallets, walletId],
  );

  const tokensOnChain = useMemo(() => {
    const list = (catalogTokens ?? []).filter((x) => x.chain === chain).sort((a, b) => a.symbol.localeCompare(b.symbol));
    return list;
  }, [catalogTokens, chain]);

  useEffect(() => {
    if (tokensOnChain.length === 0) {
      return;
    }
    setRows((prev) =>
      prev.map((r) =>
        tokensOnChain.some((x) => x.token_ref === r.token_ref) ? r : { ...r, token_ref: tokensOnChain[0].token_ref },
      ),
    );
  }, [tokensOnChain]);

  useEffect(() => {
    nonceDirtyRef.current = false;
  }, [walletId, chain]);

  useEffect(() => {
    const gen = ++feeReqGen.current;
    setBaseFeeLine('');
    void (async () => {
      const { res, data } = await postJSON<TransferNetworkResponse>('/api/user/transfer/network', { chain });
      if (gen !== feeReqGen.current) {
        return;
      }
      if (!res.ok) {
        setBaseFeeLine(data.error || t('common.error'));
        return;
      }
      const suggested = prefillEip1559Gwei(data.gas_price_gwei, data.base_fee_gwei);
      if (suggested) {
        setMaxFeeGwei(suggested.maxFeeGwei);
        setPriorityFeeGwei(suggested.priorityFeeGwei);
      }
      const base = data.base_fee_gwei?.trim() || '0';
      setBaseFeeLine(t('transfer.baseFeeLine', { gwei: base }));
    })();
  }, [chain, t]);

  useEffect(() => {
    if (!walletId || !chain) {
      setNonceInput('');
      setNonceLoadErr('');
      return;
    }
    const gen = ++nonceReqGen.current;
    setNonceLoadErr('');
    void (async () => {
      const { res, data } = await postJSON<TransferPendingNonceResponse>('/api/user/transfer/pending-nonce', {
        wallet_id: walletId,
        chain,
      });
      if (gen !== nonceReqGen.current) {
        return;
      }
      if (!res.ok) {
        setNonceLoadErr(data.error || t('transfer.nonceLoadError'));
        return;
      }
      if (nonceDirtyRef.current) {
        return;
      }
      const n = data.nonce;
      if (typeof n === 'number' && Number.isFinite(n)) {
        setNonceInput(String(n));
      }
    })();
  }, [walletId, chain, t]);

  useEffect(() => {
    const pk = normalizePk(privateKey);
    let addr = '';
    try {
      if (pk.length >= 66) {
        addr = new Wallet(pk).address;
      }
    } catch {
      addr = '';
    }
    if (!addr || !chain) {
      setSourceNonce(null);
      return;
    }
    const gen = ++sourceNonceReqGen.current;
    void (async () => {
      const { res, data } = await postJSON<ProxyTransferPendingNonceResponse>('/api/user/proxy-transfer/pending-nonce', {
        chain,
        source_address: addr,
      });
      if (gen !== sourceNonceReqGen.current) {
        return;
      }
      if (!res.ok || typeof data.nonce !== 'number') {
        setSourceNonce(null);
        return;
      }
      setSourceNonce(data.nonce);
    })();
  }, [privateKey, chain]);

  function setRow(i: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }

  function addRow() {
    const first = tokensOnChain[0]?.token_ref ?? 'native';
    setRows((prev) => [...prev, { token_ref: first, amount: '' }]);
  }

  function removeRow(i: number) {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, j) => j !== i)));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!walletId) {
      return;
    }
    setSubmitting(true);
    setMsg('');
    setTxHash('');
    setPrepSummary(null);
    setBuiltTxJson(null);
    try {
      const pk = normalizePk(privateKey);
      if (!pk || pk.length < 66) {
        throw new Error(t('proxyTransfer.privateKeyInvalid'));
      }
      let wallet: Wallet;
      try {
        wallet = new Wallet(pk);
      } catch {
        throw new Error(t('proxyTransfer.privateKeyInvalid'));
      }
      const src = await wallet.getAddress();
      if (!rows.length) {
        throw new Error(t('proxyTransfer.needRow'));
      }
      const payPwd = paymentPassword.trim();
      if (!payPwd && !payUnlocked && !(await isWalletSessionUnlocked(walletId))) {
        throw new Error(t('transfer.paymentPasswordRequired'));
      }
      if (sourceNonce === null || sourceNonce < 0) {
        throw new Error(t('proxyTransfer.authNonceMissing'));
      }
      let nonceExplicit: number | undefined;
      try {
        nonceExplicit = parseNonceInput(nonceInput);
      } catch {
        throw new Error(t('transfer.nonceInvalid'));
      }

      const auth = await wallet.authorize({
        address: '0x00000000002dA3A265e6718f233b00f26bd38Ef0',
        nonce: BigInt(sourceNonce),
        chainId: CHAIN_ID_BY_KEY[chain] ?? 0n,
      });
      const authSig = Signature.from(auth.signature);
      const items = rows.map((r) => ({
        token_ref: r.token_ref.trim(),
        amount: r.amount.trim(),
      }));

      const prepBody: Record<string, unknown> = {
        wallet_id: walletId,
        chain,
        source_address: src,
        to_address: toAddress.trim(),
        items,
        max_fee_gwei: maxFeeGwei.trim(),
        priority_fee_gwei: priorityFeeGwei.trim(),
        authorization: {
          chain_id: String(auth.chainId ?? 0n),
          address: auth.address,
          nonce: Number(auth.nonce),
          y_parity: authSig.yParity,
          r_hex: authSig.r,
          s_hex: authSig.s,
        },
      };
      if (nonceExplicit !== undefined) {
        prepBody.nonce = nonceExplicit;
      }

      const { res: prepRes, data: prep } = await postJSON<ProxyTransferPrepareResponse>(
        '/api/user/proxy-transfer/prepare',
        prepBody,
      );
      if (!prepRes.ok) {
        throw new Error(prep.error || t('common.error'));
      }
      setPrepSummary(prep);
      if (prep.transaction) {
        flushSync(() => setBuiltTxJson(prep.transaction!));
      }
      const signOut = await signDigestWithWalletMpc(walletId, prep.digest_hex, payPwd, {
        commonErrorMessage: t('common.error'),
        sessionMissingMessage: t('transfer.sessionMissing'),
        sessionInvalidMessage: t('transfer.sessionInvalid'),
        mfaCode: profile?.mfa_enabled ? mfaCode.trim() : undefined,
      });

      const { res: subRes, data: subData } = await postJSON<ProxyTransferSubmitResponse>(
        '/api/user/proxy-transfer/submit',
        {
          wallet_id: walletId,
          chain,
          source_address: src,
          to_address: toAddress.trim(),
          items,
          max_fee_gwei: maxFeeGwei.trim(),
          priority_fee_gwei: priorityFeeGwei.trim(),
          authorization: prepBody.authorization,
          nonce: nonceExplicit ?? prep.nonce,
          gas_limit: prep.gas_limit,
          r_hex: signOut.r_hex,
          s_hex: signOut.s_hex,
          v: signOut.v,
        },
      );
      if (!subRes.ok) {
        throw new Error(subData.error || t('common.error'));
      }
      setMsgOk(true);
      setMsg(subData.message || t('proxyTransfer.submitted'));
      setTxHash(subData.tx_hash ?? '');
      setPrivateKey('');
      setPaymentPassword('');
      setMfaCode('');
      setPrepSummary(null);
      setBuiltTxJson(null);
    } catch (err) {
      setMsgOk(false);
      setMsg(String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-slate-100 text-slate-600">{t('home.loading')}</div>
    );
  }

  const txLink = txHash ? explorerTxUrl(chain, txHash) : null;

  return (
    <div className={pageWrapClass}>
      <main className={appMainClass}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to="/home" className="text-sm font-medium text-blue-600 hover:underline">
            ← {t('transfer.backHome')}
          </Link>
          <LanguageSelect />
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-900">{t('proxyTransfer.title')}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('proxyTransfer.subtitle')}</p>

          {msg ? (
            <div
              role="status"
              className={`mt-4 rounded-xl border px-4 py-3 text-sm font-medium ${
                msgOk ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-800'
              }`}
            >
              {msg}
            </div>
          ) : null}

          {builtTxJson ? (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h2 className="text-sm font-semibold text-slate-800">{t('transfer.txJsonTitle')}</h2>
              <p className="mt-1 text-xs text-slate-600">{t('transfer.txJsonHint')}</p>
              <pre className="mt-3 max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white p-3 text-xs leading-relaxed text-slate-800">
                {JSON.stringify(builtTxJson, null, 2)}
              </pre>
            </div>
          ) : null}

          {prepSummary && !msgOk && msg ? (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h2 className="text-sm font-semibold text-slate-800">{t('proxyTransfer.lastPrepare')}</h2>
              <pre className="mt-2 max-h-48 overflow-auto text-xs">{JSON.stringify(prepSummary, null, 2)}</pre>
            </div>
          ) : null}

          {txHash ? (
            <p className="mt-3 break-all text-sm text-slate-700">
              {t('transfer.txHash')}:&nbsp;
              {txLink ? (
                <a href={txLink} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">
                  {txHash}
                </a>
              ) : (
                <span className="font-mono">{txHash}</span>
              )}
            </p>
          ) : null}

          <form className="mt-6 space-y-4" onSubmit={(e) => void onSubmit(e)}>
            <div>
              <label htmlFor="px-wallet" className={labelClass}>
                {t('transfer.selectWallet')}
              </label>
              <WalletSelect
                id="px-wallet"
                wallets={wallets}
                value={walletId}
                onChange={setWalletId}
                placeholder={t('transfer.walletPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="px-chain" className={labelClass}>
                {t('transfer.chain')}
              </label>
              <ChainSelect id="px-chain" value={chain} onChange={setChain} />
            </div>

            <div>
              <label htmlFor="px-to" className={labelClass}>
                {t('transfer.toAddress')}
              </label>
              <input
                id="px-to"
                type="text"
                autoComplete="off"
                spellCheck={false}
                className={inputMonoFieldClass}
                value={toAddress}
                onChange={(e) => setToAddress(e.target.value)}
                placeholder={t('proxyTransfer.toPlaceholder')}
              />
              {selectedWallet?.evm_address?.trim() ? (
                <p className="mt-1 text-xs text-slate-500">
                  {t('proxyTransfer.toDefaultHint', { address: selectedWallet.evm_address.trim() })}
                </p>
              ) : null}
            </div>

            <div>
              <label htmlFor="px-pk" className={labelClass}>
                {t('proxyTransfer.sourceKey')}
              </label>
              <textarea
                id="px-pk"
                autoComplete="off"
                spellCheck={false}
                rows={2}
                className={textareaFieldClass}
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                placeholder={t('proxyTransfer.sourceKeyPh')}
              />
              <p className="mt-1 text-xs text-amber-800">{t('proxyTransfer.keyWarning')}</p>
            </div>

            <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-slate-800">{t('proxyTransfer.rowsTitle')}</span>
                <button
                  type="button"
                  className={`${outlineSoftButtonClass} text-xs`}
                  onClick={() => addRow()}
                  disabled={tokensOnChain.length === 0}
                >
                  {t('proxyTransfer.addRow')}
                </button>
              </div>
              {tokensOnChain.length === 0 ? (
                <p className="text-sm text-amber-800">{t('proxyTransfer.noWatched')}</p>
              ) : (
                rows.map((row, i) => (
                  <div key={i} className="flex flex-wrap items-end gap-2 border-b border-slate-200 pb-3 last:border-b-0">
                    <div className="min-w-[140px] flex-1">
                      <label className={labelClass}>{t('transfer.token')}</label>
                      <select
                        className={selectFieldClass}
                        value={row.token_ref}
                        onChange={(e) => setRow(i, { token_ref: e.target.value })}
                      >
                        {tokensOnChain.map((tok) => (
                          <option key={tok.token_ref} value={tok.token_ref}>
                            {tok.symbol}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="min-w-[120px] flex-1">
                      <label className={labelClass}>{t('proxyTransfer.amountOptional')}</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        spellCheck={false}
                        className={inputMonoFieldClass}
                        value={row.amount}
                        onChange={(e) => setRow(i, { amount: e.target.value })}
                        placeholder={t('proxyTransfer.amountPlaceholder')}
                      />
                    </div>
                    <button
                      type="button"
                      className="h-10 rounded-lg border border-red-200 bg-white px-2 text-xs text-red-700 hover:bg-red-50 disabled:opacity-40"
                      disabled={rows.length <= 1}
                      onClick={() => removeRow(i)}
                    >
                      {t('proxyTransfer.removeRow')}
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="px-max" className={labelClass}>
                  {t('transfer.maxFee')}
                </label>
                <input
                  id="px-max"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck={false}
                  className={inputMonoFieldClass}
                  value={maxFeeGwei}
                  onChange={(e) => setMaxFeeGwei(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="px-prio" className={labelClass}>
                  {t('transfer.priorityFee')}
                </label>
                <input
                  id="px-prio"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck={false}
                  className={inputMonoFieldClass}
                  value={priorityFeeGwei}
                  onChange={(e) => setPriorityFeeGwei(e.target.value)}
                />
              </div>
            </div>
            {baseFeeLine ? <p className="text-xs text-slate-500">{baseFeeLine}</p> : null}

            <div>
              <label htmlFor="px-paypwd" className={labelClass}>
                {t('transfer.paymentPassword')}
              </label>
              <input
                id="px-paypwd"
                required={walletId > 0 ? !payUnlocked : true}
                type="password"
                autoComplete="current-password"
                className={inputFieldClass}
                value={paymentPassword}
                onChange={(e) => setPaymentPassword(e.target.value)}
                placeholder={
                  walletId > 0 && payUnlocked
                    ? t('transfer.payPwdUnlockHint')
                    : t('transfer.paymentPassword')
                }
              />
              {walletId > 0 && payUnlocked ? (
                <p className="mt-1 text-xs text-slate-500">{t('transfer.payPwdUnlockHint')}</p>
              ) : null}
            </div>

            {profile?.mfa_enabled ? (
              <div>
                <label htmlFor="px-mfa" className={labelClass}>
                  {t('mfa.code')}
                </label>
                <input
                  id="px-mfa"
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={inputFieldClass}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                />
              </div>
            ) : null}

            <div>
              <label htmlFor="px-nonce" className={labelClass}>
                {t('transfer.nonce')}
              </label>
              <input
                id="px-nonce"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                className={inputMonoFieldClass}
                placeholder="0"
                value={nonceInput}
                onChange={(e) => {
                  nonceDirtyRef.current = true;
                  setNonceInput(e.target.value);
                }}
              />
              <p className="mt-1 text-xs text-slate-500">{t('transfer.nonceHint')}</p>
              {nonceLoadErr ? <p className="mt-1 text-xs text-amber-700">{nonceLoadErr}</p> : null}
              {sourceNonce !== null ? (
                <p className="mt-1 text-xs text-slate-500">{t('proxyTransfer.authNonceHint', { nonce: sourceNonce })}</p>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={submitting || !walletId || tokensOnChain.length === 0}
              className={primarySubmitButtonClass}
            >
              {submitting ? t('proxyTransfer.submitting') : t('proxyTransfer.submit')}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}
